import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeftRight, CheckCircle, XCircle, Building2, RefreshCw, Loader2, History, Zap } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import PhaseGToolsDialog from '@/components/banking/PhaseGToolsDialog';
import { localToday, toLocalYmd } from '@/lib/dateRange';

function fmt(v) { return (v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function BankReconciliationPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bankBalance, setBankBalance] = useState('');
  const [statementDate, setStatementDate] = useState(localToday());
  const [notes, setNotes] = useState('');
  const [matching, setMatching] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [windowDays, setWindowDays] = useState(3);
  const [autoMatchResult, setAutoMatchResult] = useState(null);
  // iter623 — Phase G tools dialog. Picks up the most recent statement
  // for the active account and offers bulk-reconcile, smart-match
  // preview, and a running cash-position chart.
  const [phaseGOpen, setPhaseGOpen] = useState(false);
  const [phaseGStmt, setPhaseGStmt] = useState(null);
  const openPhaseG = async () => {
    try {
      const { data: stmts } = await api.get('/bank-statements');
      const latest = (stmts || [])[0];
      if (!latest) {
        toast.error('No bank statements found. Import one first.');
        return;
      }
      setPhaseGStmt(latest);
      setPhaseGOpen(true);
    } catch {
      toast.error('Failed to load statements.');
    }
  };
  // iter410 — Default to last 90 days instead of "today only" so the
  // page lands with data instead of empty rows. Default account is
  // still 1010 (Cash) but if that's empty the page now auto-tries the
  // first bank account from /accounting/accounts. Users complained
  // "page shows nothing" — root cause was both defaults yielded zero
  // rows for tenants whose recent activity sits on bank accounts
  // (012-1/012-2) over a wider window.
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return toLocalYmd(d);
  });
  const [dateTo, setDateTo] = useState(localToday());
  const [account, setAccount] = useState('1010');
  const [accountsLoaded, setAccountsLoaded] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ account });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await api.get(`/accounting/reconciliation/items?${params}`);
      setData(res.data);
      setSelected(new Set());
      // iter410 — If the default account 1010 (Cash) is empty for this
      // tenant, try to auto-switch to the first bank-type account so
      // the page shows real data on first load. Only fires once
      // (`accountsLoaded` gates re-entry) so manual account picks stick.
      if (!accountsLoaded && account === '1010' && (res.data?.items || []).length === 0) {
        setAccountsLoaded(true);
        try {
          const acc = await api.get('/accounting/accounts');
          const list = Array.isArray(acc.data) ? acc.data : (acc.data?.items || []);
          // Prefer a leaf bank account (`012-N` or `1020-N`) over a
          // parent group account so we land on a real ledger. Fall back
          // through: 012-2 (Bank Main) → any 012-X leaf → any "bank" name.
          const isLeaf = (c) => /^\d{3,4}-\d+/.test(c || '');
          const bank = list.find(a => a.code === '012-2')
            || list.find(a => isLeaf(a.code) && (a.code || '').startsWith('012'))
            || list.find(a => isLeaf(a.code) && /bank/i.test(a.name || ''))
            || null;
          if (bank?.code && bank.code !== account) {
            toast.info(`No entries on ${account} — switching to ${bank.code} ${bank.name || ''}`);
            setAccount(bank.code);
          }
        } catch { /* no-op */ }
      }
    } catch { toast.error('Failed to load reconciliation data'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [account]);

  const toggleItem = (entryId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId); else next.add(entryId);
      return next;
    });
  };

  const selectAllUnreconciled = () => {
    const unreconciled = (data?.items || []).filter(i => !i.reconciled).map(i => i.entry_id);
    setSelected(new Set(unreconciled));
  };

  const matchEntries = async () => {
    if (selected.size === 0) { toast.error('Select entries to reconcile'); return; }
    setMatching(true);
    try {
      await api.post('/accounting/reconciliation/match', {
        entry_ids: Array.from(selected),
        bank_balance: parseFloat(bankBalance) || 0,
        statement_date: statementDate,
        notes,
      });
      toast.success(`Reconciled ${selected.size} entries`);
      fetchData();
      setNotes('');
    } catch { toast.error('Reconciliation failed'); }
    setMatching(false);
  };

  const unmatchEntries = async (entryIds) => {
    try {
      await api.post('/accounting/reconciliation/unmatch', { entry_ids: entryIds });
      toast.success('Entries un-reconciled');
      fetchData();
    } catch { toast.error('Failed to un-reconcile'); }
  };

  const runAutoMatch = async () => {
    if (!window.confirm(
      `Run AGGRESSIVE auto-match on account ${account}?\n\nThis pairs unreconciled journal entries with bank-statement transactions by amount alone within ±${windowDays} day(s). You can undo any match individually below.`,
    )) return;
    setAutoMatching(true);
    setAutoMatchResult(null);
    try {
      const res = await api.post('/accounting/reconciliation/auto-match', {
        account,
        window_days: Number(windowDays) || 3,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      const d = res.data || {};
      setAutoMatchResult(d);
      const count = d.match_count || 0;
      if (count > 0) {
        toast.success(`Auto-matched ${count} entries (scanned ${d.je_candidates_scanned} JEs · ${d.bank_txn_candidates_scanned} bank txns)`);
      } else {
        toast(`No matches — scanned ${d.je_candidates_scanned} JEs · ${d.bank_txn_candidates_scanned} bank txns within ±${windowDays}d`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-match failed');
    }
    setAutoMatching(false);
  };

  const undoLastAutoMatch = async () => {
    if (!autoMatchResult?.matches?.length) return;
    if (!window.confirm(`Undo all ${autoMatchResult.matches.length} auto-matched entries from this run?`)) return;
    try {
      await api.post('/accounting/reconciliation/unmatch', {
        entry_ids: autoMatchResult.matches.map(m => m.entry_id),
      });
      toast.success('Undone — entries are unreconciled again');
      setAutoMatchResult(null);
      fetchData();
    } catch { toast.error('Undo failed'); }
  };

  const items = data?.items || [];
  const selectedTotal = items.filter(i => selected.has(i.entry_id)).reduce((s, i) => s + i.amount, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="bank-reconciliation-page">
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="bank-reconciliation"
          title="What is Bank Reconciliation?"
        >
          <p>
            Side-by-side comparison of your <strong>journal entries</strong> against the <strong>bank statement</strong> for each account. Tick the matching pairs to mark them reconciled — the running balance updates live so you can see the unreconciled gap shrink to zero. Use this when you need a formal sign-off (auditors love it) rather than the lighter auto-matching in Bank Statements.
          </p>
        </PageInfoCallout>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">
              <ArrowLeftRight size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Bank Reconciliation</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Match journal entries with bank statements</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw size={14} /></Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Account</label>
                <select value={account} onChange={e => setAccount(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="account-selector">
                  <option value="1010">1010 - Bank</option>
                  <option value="1000">1000 - Cash</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">From</label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="recon-date-from" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">To</label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="recon-date-to" />
              </div>
              <Button size="sm" onClick={fetchData}>Filter</Button>
              <div className="flex items-center gap-1.5 ml-auto">
                <label className="text-xs text-muted-foreground">Auto-match window</label>
                <Input
                  type="number" min={1} max={30}
                  value={windowDays}
                  onChange={e => setWindowDays(e.target.value)}
                  className="w-16 h-9 text-xs"
                  data-testid="auto-match-window"
                />
                <span className="text-xs text-muted-foreground">days</span>
                <Button
                  size="sm"
                  onClick={runAutoMatch}
                  disabled={autoMatching}
                  className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white"
                  data-testid="auto-match-btn"
                  title="Pair unreconciled JEs to bank statement txns by amount within ±N days. Aggressive — review then undo if needed."
                >
                  {autoMatching ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                  Auto-Match
                </Button>
                {/* iter623 — Phase G Tools (bulk + smart-match preview + cash chart). */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openPhaseG}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="phase-g-open-btn"
                  title="Bulk reconcile, smart-match preview, and cash-position chart for the most recent statement"
                >
                  <Zap size={14} className="mr-1" />
                  Phase G tools
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Auto-match result / Undo */}
        {autoMatchResult && (
          <Card className="border-violet-200 bg-violet-50/40" data-testid="auto-match-result">
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <Zap size={16} className="text-violet-600" />
              <div className="text-sm flex-1">
                <strong className="text-violet-700">Auto-match run:</strong>{' '}
                {autoMatchResult.match_count > 0 ? (
                  <span>Matched <strong>{autoMatchResult.match_count}</strong> entries from {autoMatchResult.je_candidates_scanned} JE candidate(s) · {autoMatchResult.bank_txn_candidates_scanned} bank txn(s).</span>
                ) : (
                  <span className="text-muted-foreground">No matches — try increasing the window or check that bank statements are uploaded for this period.</span>
                )}
              </div>
              {autoMatchResult.match_count > 0 && (
                <Button variant="outline" size="sm" onClick={undoLastAutoMatch} data-testid="auto-match-undo">
                  <XCircle size={13} className="mr-1" /> Undo this run
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setAutoMatchResult(null)} data-testid="auto-match-dismiss">
                Dismiss
              </Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card><CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Total Debits</p>
                <p className="text-lg font-bold text-success" data-testid="total-debits">{fmt(data.total_debits)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Total Credits</p>
                <p className="text-lg font-bold text-error" data-testid="total-credits">{fmt(data.total_credits)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Net Balance</p>
                <p className="text-lg font-bold text-foreground">{fmt(data.net_balance)}</p>
              </CardContent></Card>
              <Card className="border-success/30"><CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Reconciled</p>
                <p className="text-lg font-bold text-success">{data.reconciled_count}</p>
              </CardContent></Card>
              <Card className="border-warning/30"><CardContent className="p-3 text-center">
                <p className="text-[10px] text-muted-foreground uppercase">Unreconciled</p>
                <p className="text-lg font-bold text-warning" data-testid="unreconciled-count">{data.unreconciled_count}</p>
              </CardContent></Card>
            </div>

            {/* Reconciliation Form */}
            <Card className="border-indigo-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Building2 size={14} /> Match with Bank Statement</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Statement Date</label>
                    <Input type="date" value={statementDate} onChange={e => setStatementDate(e.target.value)} className="w-40" data-testid="statement-date" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Bank Balance</label>
                    <Input type="number" step="0.01" placeholder="0.00" value={bankBalance} onChange={e => setBankBalance(e.target.value)} className="w-40" data-testid="bank-balance-input" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-muted-foreground block mb-1">Notes</label>
                    <Input placeholder="Optional notes..." value={notes} onChange={e => setNotes(e.target.value)} data-testid="recon-notes" />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Selected: <strong>{selected.size}</strong> entries &middot; Net: <strong className={selectedTotal >= 0 ? 'text-success' : 'text-error'}>{fmt(selectedTotal)}</strong>
                    {bankBalance && <span> &middot; Difference: <strong className={Math.abs(selectedTotal - parseFloat(bankBalance)) < 1 ? 'text-success' : 'text-error'}>{fmt(selectedTotal - parseFloat(bankBalance))}</strong></span>}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllUnreconciled} data-testid="select-all-btn">Select All</Button>
                    <Button size="sm" onClick={matchEntries} disabled={matching || selected.size === 0} data-testid="reconcile-btn">
                      {matching ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                      Reconcile ({selected.size})
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Entries Table */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Journal Entry Lines</CardTitle></CardHeader>
              <CardContent className="p-0">
                {items.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">No journal entries found for this account in the selected period</div>
                ) : (
                  <table className="w-full text-sm" data-testid="entries-table">
                    <thead className="bg-muted/50"><tr>
                      <th className="w-10 px-3 py-3"></th>
                      <th className="text-left px-3 py-3 font-medium text-muted-foreground">Date</th>
                      <th className="text-left px-3 py-3 font-medium text-muted-foreground">Entry #</th>
                      <th className="text-left px-3 py-3 font-medium text-muted-foreground">Description</th>
                      <th className="text-right px-3 py-3 font-medium text-muted-foreground">Debit</th>
                      <th className="text-right px-3 py-3 font-medium text-muted-foreground">Credit</th>
                      <th className="text-center px-3 py-3 font-medium text-muted-foreground">Status</th>
                      <th className="w-10 px-3 py-3"></th>
                    </tr></thead>
                    <tbody>
                      {items.map((item, i) => (
                        <tr key={i} className={`border-t border-border hover:bg-muted/50 ${selected.has(item.entry_id) ? 'bg-indigo-50/30' : ''}`}>
                          <td className="px-3 py-2.5">
                            {!item.reconciled && (
                              <Checkbox checked={selected.has(item.entry_id)} onCheckedChange={() => toggleItem(item.entry_id)} data-testid={`checkbox-${i}`} />
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">{item.date?.slice(0, 10)}</td>
                          <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{item.entry_number}</td>
                          <td className="px-3 py-2.5 text-foreground max-w-[250px] truncate">{item.description}</td>
                          <td className="px-3 py-2.5 text-right text-success font-medium">{item.debit > 0 ? fmt(item.debit) : '-'}</td>
                          <td className="px-3 py-2.5 text-right text-error font-medium">{item.credit > 0 ? fmt(item.credit) : '-'}</td>
                          <td className="px-3 py-2.5 text-center">
                            {item.reconciled ? (
                              <Badge className="bg-success/20 text-success text-[10px]"><CheckCircle size={10} className="mr-0.5" /> Matched</Badge>
                            ) : (
                              <Badge className="bg-warning/20 text-warning text-[10px]">Pending</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {item.reconciled && (
                              <button onClick={() => unmatchEntries([item.entry_id])} className="text-muted-foreground hover:text-error" title="Un-reconcile">
                                <XCircle size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>

            {/* Recent Reconciliations */}
            {(data.recent_reconciliations || []).length > 0 && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><History size={14} /> Recent Reconciliations</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {data.recent_reconciliations.map(r => (
                    <div key={r.id} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-2 text-sm">
                      <div>
                        <span className="font-medium">{r.statement_date}</span>
                        <span className="text-muted-foreground ml-2">&middot; {r.entries_count} entries</span>
                        {r.notes && <span className="text-muted-foreground ml-2">&middot; {r.notes}</span>}
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-foreground">Bank: {fmt(r.bank_balance)}</span>
                        <span className="text-muted-foreground text-xs ml-2">by {r.reconciled_by}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
        <PhaseGToolsDialog
          open={phaseGOpen}
          onClose={() => setPhaseGOpen(false)}
          statementId={phaseGStmt?.id}
          statementName={phaseGStmt ? `${phaseGStmt.bank_name} · ${phaseGStmt.statement_date}` : ''}
        />
      </div>
    </DashboardLayout>
  );
}
